import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Globe, User, Settings, LogOut, CreditCard, TestTube, BarChart3, Heart, Sparkles, Users } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { LogoComplete, LogoSimple } from "@/components/Logo";

export function Header() {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, isLoading, clearUser } = useAuth();
  const [location] = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showNavLogo, setShowNavLogo] = useState(false);
  const queryClient = useQueryClient();

  // Function to translate category names from English to Spanish
  const translateCategory = (category: string) => {
    const categoryMap: { [key: string]: string } = {
      'politics': t('statistics.politics'),
      'society': t('statistics.society'),
      'economy': t('statistics.economy'),
      'culture': t('statistics.culture'),
      'technology': t('statistics.technology'),
      'entertainment': t('statistics.entertainment'),
      'sports': t('statistics.sports'),
      'science': t('statistics.science'),
      'lifestyle': t('statistics.lifestyle'),
      'unclassified': t('statistics.unclassified')
    };
    
    return categoryMap[category] || category;
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const navigation: Array<{ name: string; href: string; isExternal?: boolean }> = [];

  // Only show main navigation on home page
  const isHomePage = location === '/' || location === '';
  const showMainNavigation = isHomePage;
  
  // Show navbar logo on internal pages (not home page)
  const isInternalPage = !isHomePage;

  const testingMenu = [
    { name: 'Test Stripe', href: '/test-stripe' },
  ];

  const scrollToSection = (href: string, isExternal?: boolean) => {
    if (isExternal || !href.startsWith('#')) {
      window.location.href = href;
    } else {
      const element = document.querySelector(href);
      if (element) {
        const headerHeight = 80; // Height of the fixed header with some padding
        const elementPosition = (element as HTMLElement).offsetTop;
        const offsetPosition = elementPosition - headerHeight;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
    setMobileMenuOpen(false);
  };

  // Effect to control navbar logo visibility based on scroll
  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          // Get the main logo element by ID for better performance
          const mainLogo = document.getElementById('main-logo');
          
          if (mainLogo) {
            const rect = mainLogo.getBoundingClientRect();
            const logoTop = rect.top;
            
            // Show navbar logo ONLY when main logo starts going above viewport
            // logoTop < 0 means the logo is starting to go above the visible area
            const shouldShow = logoTop < 0;
            setShowNavLogo(shouldShow);
          }
          
          ticking = false;
        });
        
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className="bg-white dark:bg-slate-900 shadow-sm border-b border-slate-200 dark:border-slate-700 fixed top-0 z-50 w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo - Appears when main logo scrolls up OR on internal pages */}
          <div 
            id="logo-nav" 
            className={`flex items-center transition-all duration-300 ease-in-out ${
              (showNavLogo || isInternalPage)
                ? 'opacity-100 transform translate-y-0' 
                : 'opacity-0 transform -translate-y-2 pointer-events-none'
            }`}
          >
            <Link href="/" className="hover:opacity-80 transition-opacity">
              <div className="flex items-center">
                <img 
                  src="/images/logo-complete.png" 
                  alt="Statemia" 
                  className="h-6 w-auto object-contain"
                  width="120"
                  height="24"
                  onError={(e) => {
                    // Fallback to gradient if image fails to load
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) {
                      fallback.style.display = 'flex';
                    }
                  }}
                />
                {/* Fallback gradient logo */}
                <div 
                  className="h-6 w-6 bg-gradient-to-r from-primary to-secondary rounded-lg flex items-center justify-center mr-2"
                  style={{ display: 'none' }}
                >
                  <span className="text-white font-bold text-xs">S</span>
                </div>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation - Only on home page */}
          {showMainNavigation && (
            <nav className="hidden md:flex space-x-8">
              {navigation.map((item) => (
                <button
                  key={item.name}
                  onClick={() => scrollToSection(item.href, item.isExternal)}
                  className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white px-3 py-2 text-sm font-medium transition-colors"
                >
                  {item.name}
                </button>
              ))}
            </nav>
          )}

          {/* Right side items */}
          <div className="flex items-center space-x-4">
            {/* Testing Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="hidden items-center">
                  <TestTube className="w-4 h-4 mr-1" />
                  Tests
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {testingMenu.map((item) => (
                  <DropdownMenuItem key={item.name} asChild>
                    <Link href={item.href}>
                      {item.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>



            {/* Desktop User Menu - Always visible on desktop */}
            <div className="hidden lg:block">
              {!isLoading && isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user?.profileImageUrl || undefined} />
                        <AvatarFallback className="text-xs">
                          {user?.firstName?.[0] || user?.email?.[0]?.toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end">
                  <div className="flex items-center justify-start p-2">
                    <div className="flex flex-col space-y-1 leading-none">
                      <p className="font-medium">{user?.firstName} {user?.lastName}</p>
                      <p className="w-[200px] text-sm text-muted-foreground truncate">
                        {user?.email}
                      </p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link href="/profile" className="cursor-pointer">
                      <User className="mr-2 h-4 w-4" />
                      {t('nav.profile')}
                    </Link>
                  </DropdownMenuItem>
                  {/* Dashboard option temporarily hidden */}
                  {/* <DropdownMenuItem asChild>
                    <Link href="/dashboard" className="cursor-pointer">
                      <BarChart3 className="mr-2 h-4 w-4" />
                      {t('nav.dashboard')}
                    </Link>
                  </DropdownMenuItem> */}
                  <DropdownMenuItem asChild>
                    <Link href="/favorites" className="cursor-pointer">
                      <Heart className="mr-2 h-4 w-4" />
                      {t('nav.favorites')}
                    </Link>
                  </DropdownMenuItem>
                  {user?.role === 'admin' && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link href="/admin" className="cursor-pointer">
                          <Settings className="mr-2 h-4 w-4" />
                          {t('nav.admin')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link href="/admin/blog/new" className="cursor-pointer">
                          <BarChart3 className="mr-2 h-4 w-4" />
                          Crear entrada de blog
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link href="/statistics" className="cursor-pointer">
                          <BarChart3 className="mr-2 h-4 w-4" />
                          {t('nav.manageStatistics')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link href="/admin/experiences" className="cursor-pointer">
                          <Sparkles className="mr-2 h-4 w-4" />
                          Experiencias
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link href="/admin/authors" className="cursor-pointer">
                          <Users className="mr-2 h-4 w-4" />
                          Equipo / Autores
                        </Link>
                      </DropdownMenuItem>
                      {/* Temporarily hidden for production
                      <DropdownMenuItem asChild>
                        <Link href="/test-stripe" className="cursor-pointer">
                          <CreditCard className="mr-2 h-4 w-4" />
                          Test Stripe
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link href="/test-google-ads" className="cursor-pointer">
                          <TestTube className="mr-2 h-4 w-4" />
                          Test Google Ads
                        </Link>
                      </DropdownMenuItem>
                      */}
                      <DropdownMenuItem asChild>
                        <Link href="/analytics" className="cursor-pointer">
                          <BarChart3 className="mr-2 h-4 w-4" />
                          Analytics
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={async () => {
                      try {
                        await fetch('/api/auth/logout', { method: 'POST' });
                        clearUser();
                        // Clear all React Query cache
                        queryClient.clear();
                        // Clear any local storage or session storage
                        localStorage.clear();
                        sessionStorage.clear();
                        // Redirect to home
                        window.location.href = '/';
                      } catch (error) {
                        console.error('Error signing out:', error);
                      }
                    }}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    {t('auth.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
                </DropdownMenu>
              ) : !isLoading ? (
                <>
                  <Button variant="ghost" onClick={() => window.location.href = "/login"}>
                    {t('auth.login')}
                  </Button>
                  <Button onClick={() => window.location.href = "/login"}>
                    {t('auth.register')}
                  </Button>
                </>
              ) : (
                // Loading state - show skeleton
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse"></div>
                </div>
              )}
            </div>

            {/* Mobile/Tablet menu button */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="lg:hidden">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent>
                <div className="flex flex-col space-y-4 mt-8">
                  {/* Authentication section */}
                  {!isAuthenticated ? (
                    <div className="border-b border-slate-200 dark:border-slate-700 pb-4">
                      <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 uppercase tracking-wide">
                        {t('auth.authentication')}
                      </h3>
                      <div className="space-y-2">
                        <button
                          onClick={() => {
                            setMobileMenuOpen(false);
                            window.location.href = "/login";
                          }}
                          className="w-full text-left text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white px-3 py-2 text-sm font-medium transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                        >
                          {t('auth.login')}
                        </button>
                        <button
                          onClick={() => {
                            setMobileMenuOpen(false);
                            window.location.href = "/login";
                          }}
                          className="w-full text-left text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white px-3 py-2 text-sm font-medium transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                        >
                          {t('auth.register')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="border-b border-slate-200 dark:border-slate-700 pb-4">
                      <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 uppercase tracking-wide">
                        {user?.firstName} {user?.lastName}
                      </h3>
                      <div className="space-y-2">
                        <button
                          onClick={() => {
                            setMobileMenuOpen(false);
                            window.location.href = "/profile";
                          }}
                          className="w-full text-left text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white px-3 py-2 text-sm font-medium transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center"
                        >
                          <User className="mr-2 h-4 w-4" />
                          {t('nav.profile')}
                        </button>
                        <button
                          onClick={() => {
                            setMobileMenuOpen(false);
                            window.location.href = "/favorites";
                          }}
                          className="w-full text-left text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white px-3 py-2 text-sm font-medium transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center"
                        >
                          <Heart className="mr-2 h-4 w-4" />
                          {t('nav.favorites')}
                        </button>
                        {user?.role === 'admin' && (
                          <>
                            <button
                              onClick={() => {
                                setMobileMenuOpen(false);
                                window.location.href = "/admin";
                              }}
                              className="w-full text-left text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white px-3 py-2 text-sm font-medium transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center"
                            >
                              <Settings className="mr-2 h-4 w-4" />
                              {t('nav.admin')}
                            </button>
                            <button
                              onClick={() => {
                                setMobileMenuOpen(false);
                                window.location.href = "/admin/blog/new";
                              }}
                              className="w-full text-left text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white px-3 py-2 text-sm font-medium transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center"
                            >
                              <BarChart3 className="mr-2 h-4 w-4" />
                              Crear entrada de blog
                            </button>
                            <button
                              onClick={() => {
                                setMobileMenuOpen(false);
                                window.location.href = "/statistics";
                              }}
                              className="w-full text-left text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white px-3 py-2 text-sm font-medium transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center"
                            >
                              <BarChart3 className="mr-2 h-4 w-4" />
                              {t('nav.manageStatistics')}
                            </button>
                            <button
                              onClick={() => {
                                setMobileMenuOpen(false);
                                window.location.href = "/admin/experiences";
                              }}
                              className="w-full text-left text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white px-3 py-2 text-sm font-medium transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center"
                            >
                              <Sparkles className="mr-2 h-4 w-4" />
                              Experiencias
                            </button>
                            <button
                              onClick={() => {
                                setMobileMenuOpen(false);
                                window.location.href = "/admin/authors";
                              }}
                              className="w-full text-left text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white px-3 py-2 text-sm font-medium transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center"
                            >
                              <Users className="mr-2 h-4 w-4" />
                              Equipo / Autores
                            </button>
                            <button
                              onClick={() => {
                                setMobileMenuOpen(false);
                                window.location.href = "/analytics";
                              }}
                              className="w-full text-left text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white px-3 py-2 text-sm font-medium transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center"
                            >
                              <BarChart3 className="mr-2 h-4 w-4" />
                              Analytics
                            </button>
                          </>
                        )}
                        <button
                          onClick={async () => {
                            setMobileMenuOpen(false);
                            try {
                              await fetch('/api/auth/logout', { method: 'POST' });
                              clearUser();
                              queryClient.clear();
                              localStorage.clear();
                              sessionStorage.clear();
                              window.location.href = '/';
                            } catch (error) {
                              console.error('Error signing out:', error);
                            }
                          }}
                          className="w-full text-left text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white px-3 py-2 text-sm font-medium transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center"
                        >
                          <LogOut className="mr-2 h-4 w-4" />
                          {t('auth.logout')}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Categories for mobile/tablet */}
                  <div className="border-b border-slate-200 dark:border-slate-700 pb-4">
                    <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 uppercase tracking-wide">
                      {t('statistics.categories')}
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      {['society', 'politics', 'economy', 'culture', 'entertainment', 'sports', 'technology', 'science', 'lifestyle'].map((category) => (
                        <button
                          key={category}
                          onClick={() => {
                            setMobileMenuOpen(false);
                            window.location.href = `/statistics-list?category=${category}`;
                          }}
                          className="text-left text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white px-3 py-2 text-sm font-medium transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 capitalize"
                        >
                          {translateCategory(category)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Blog entry point */}
                  <div className="border-b border-slate-200 dark:border-slate-700 pb-4">
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        window.location.href = "/blog";
                      }}
                      className="w-full text-left text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white px-3 py-2 text-sm font-medium transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      Blog
                    </button>
                  </div>

                  {showMainNavigation && navigation.map((item) => (
                    <button
                      key={item.name}
                      onClick={() => scrollToSection(item.href, item.isExternal)}
                      className="text-left text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white px-3 py-2 text-sm font-medium transition-colors"
                    >
                      {item.name}
                    </button>
                  ))}
                  {!showMainNavigation && (
                    <div className="text-center py-4">
                      <Link 
                        href="/" 
                        className="text-primary hover:text-blue-700 font-medium"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        ← Volver al inicio
                      </Link>
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
        

      </div>
    </header>
  );
}
